import React, { useState, memo, useRef } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCreateModelConfig } from '@/stores/modelsStore'

interface NewModelConfigurationFormProps {
  closeModal: () => void
}

function NewModelConfigurationForm({
  closeModal,
}: NewModelConfigurationFormProps) {
  // Preserve all existing states
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [maxTokens, setMaxTokens] = useState(8000)
  const [topP, setTopP] = useState(0.95)
  const [temperature, setTemperature] = useState(0.7)
  const [pricing, setPricing] = useState('0.01/0.03')
  const [creating, setCreating] = useState(false)

  // Keep the existing createModelConfig hook
  const createModelConfig = useCreateModelConfig()

  // Preserve the existing handleCreate function
  const handleCreate = async () => {
    if (!name || !url) {
      toast.error('Name and URL are required')
      return
    }

    setCreating(true)
    try {
      await createModelConfig.mutateAsync({
        name,
        url,
        apiKey,
        isDefault,
        systemPrompt,
        maxTokens,
        topP,
        temperature,
        pricing,
      })

      toast.success('Model configuration created successfully')
      closeModal()
    } catch (error) {
      toast.error(error.message || 'Failed to create model configuration')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="model-name">Model Name</Label>
        <Input
          id="model-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter model name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="model-url">URL</Label>
        <Input
          id="model-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Enter model URL"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="model-apikey">API Key</Label>
        <Input
          id="model-apikey"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Enter API key (optional)"
          type="password"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="model-system-prompt">System Prompt</Label>
        <Textarea
          id="model-system-prompt"
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={5}
          placeholder="Enter system prompt"
          className="resize-y"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="model-max-tokens">Max Tokens</Label>
          <Input
            id="model-max-tokens"
            type="number"
            value={maxTokens}
            onChange={(e) => setMaxTokens(parseInt(e.target.value))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="model-temperature">Temperature</Label>
          <Input
            id="model-temperature"
            type="number"
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            step="0.01"
            min="0"
            max="1"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="model-top-p">Top P</Label>
          <Input
            id="model-top-p"
            type="number"
            value={topP}
            onChange={(e) => setTopP(parseFloat(e.target.value))}
            step="0.01"
            min="0"
            max="1"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="model-pricing">Pricing</Label>
          <Input
            id="model-pricing"
            value={pricing}
            onChange={(e) => setPricing(e.target.value)}
            placeholder="Format: input/output (e.g. 0.01/0.03)"
          />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="model-default"
          checked={isDefault}
          onCheckedChange={(checked) => setIsDefault(checked === true)}
        />
        <Label htmlFor="model-default">Set as default model</Label>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button variant="outline" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={handleCreate}
          disabled={!name || !url || creating}
        >
          {creating ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </div>
  )
}

export default memo(NewModelConfigurationForm)
